import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { kindLabel } from '../lib/places';
import type { PlaceKind } from '../lib/types';
import { ErrorNote, Field, PageHeader } from '../components/ui';

export default function OwnersPage() {
  const [params] = useSearchParams();

  return (
    <>
      <PageHeader eyebrow="For owners and locals" title="Own a backroad spot?">
        Road-trippers and locals are looking for places exactly like yours: the diner, the smokehouse, the supper club, the
        tavern. Claim your listing to keep it current, or tell us about a place we&apos;re missing.
      </PageHeader>
      <div className="mx-auto grid max-w-7xl gap-6 px-4 pb-14 sm:px-6 lg:grid-cols-2">
        <ClaimForm initialBar={params.get('place') ?? params.get('bar') ?? ''} initialTown={params.get('town') ?? ''} initialState={params.get('state') ?? ''} barId={params.get('id') ?? undefined} />
        <SuggestForm />
      </div>
    </>
  );
}

function ClaimForm({ initialBar, initialTown, initialState, barId }: { initialBar: string; initialTown: string; initialState: string; barId?: string }) {
  const [form, setForm] = useState({ barName: initialBar, town: initialTown, state: initialState, ownerName: '', email: '', phone: '', ddPerk: '', message: '' });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = useMutation({
    mutationFn: () =>
      api.claimListing({
        barId,
        barName: form.barName.trim(),
        town: form.town.trim() || undefined,
        state: form.state.trim().toUpperCase() || undefined,
        ownerName: form.ownerName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        ddPerk: form.ddPerk.trim() || undefined,
        message: form.message.trim() || undefined,
      }),
  });
  const errors = submit.error instanceof ApiError ? submit.error.fieldErrors : {};

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit.mutate();
  };

  return (
    <section className="card flex flex-col gap-4 p-6" aria-labelledby="claimH">
      <h2 id="claimH" className="text-3xl font-black">Claim your listing</h2>
      <ul className="list-disc space-y-1 pl-5 text-[15px] text-[#d6d2c2]">
        <li>Update your hours, specials and photos</li>
        <li>Get featured on a route, or sponsor a passport prize</li>
        <li>Run a bar? Offer a DD perk, like free pop or a food deal for the driver</li>
      </ul>

      {submit.isSuccess ? (
        <p className="chip chip-ok self-start px-4 py-2 text-base" role="status">
          Thanks! We emailed you a confirmation and will be in touch.
        </p>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <Field label="Place name" htmlFor="c-bar" error={errors.barName}>
            <input id="c-bar" type="text" value={form.barName} onChange={set('barName')} required />
          </Field>
          <div className="grid gap-4 sm:grid-cols-[1fr_100px]">
            <Field label="Town" htmlFor="c-town" error={errors.town}>
              <input id="c-town" type="text" value={form.town} onChange={set('town')} />
            </Field>
            <Field label="State" htmlFor="c-state" error={errors.state}>
              <input id="c-state" type="text" value={form.state} onChange={set('state')} maxLength={2} placeholder="MI" />
            </Field>
          </div>
          <Field label="Your name" htmlFor="c-name" error={errors.ownerName}>
            <input id="c-name" type="text" value={form.ownerName} onChange={set('ownerName')} required autoComplete="name" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Email" htmlFor="c-email" error={errors.email}>
              <input id="c-email" type="email" value={form.email} onChange={set('email')} required autoComplete="email" />
            </Field>
            <Field label="Phone (optional)" htmlFor="c-phone" error={errors.phone}>
              <input id="c-phone" type="tel" value={form.phone} onChange={set('phone')} autoComplete="tel" />
            </Field>
          </div>
          <Field label="DD perk you'd offer (bars, optional)" htmlFor="c-perk" error={errors.ddPerk}>
            <input id="c-perk" type="text" value={form.ddPerk} onChange={set('ddPerk')} placeholder="Free pop and fries for the driver" />
          </Field>
          <Field label="Anything else? (optional)" htmlFor="c-msg" error={errors.message}>
            <textarea id="c-msg" value={form.message} onChange={set('message')} />
          </Field>
          <button type="submit" className="btn btn-primary self-start" disabled={submit.isPending}>
            {submit.isPending ? 'Sending…' : 'Claim my listing'}
          </button>
          {submit.error && !Object.keys(errors).length && <ErrorNote error={submit.error} />}
        </form>
      )}
    </section>
  );
}

const KINDS: PlaceKind[] = ['Bar', 'Diner', 'Bbq', 'Cafe', 'DriveIn', 'Bakery', 'IceCream', 'SupperClub', 'Restaurant'];

function SuggestForm() {
  const [form, setForm] = useState({ name: '', kind: 'Bar' as PlaceKind, town: '', state: '', address: '', whyItsGreat: '', submitterName: '', submitterEmail: '' });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = useMutation({
    mutationFn: () =>
      api.suggestPlace({
        name: form.name.trim(),
        kind: form.kind,
        town: form.town.trim(),
        state: form.state.trim().toUpperCase(),
        address: form.address.trim() || undefined,
        whyItsGreat: form.whyItsGreat.trim() || undefined,
        submitterName: form.submitterName.trim() || undefined,
        submitterEmail: form.submitterEmail.trim() || undefined,
      }),
  });
  const errors = submit.error instanceof ApiError ? submit.error.fieldErrors : {};

  return (
    <section className="card flex flex-col gap-4 p-6" aria-labelledby="suggestH">
      <h2 id="suggestH" className="text-3xl font-black">Know a spot we&apos;re missing?</h2>
      <p className="text-[15px] text-[#d6d2c2]">The best backroad places barely have a web presence. Tell us about one.</p>

      {submit.isSuccess ? (
        <p className="chip chip-ok self-start px-4 py-2 text-base" role="status">
          Thanks! We&apos;ll check it out.
        </p>
      ) : (
        <form
          className="flex flex-col gap-4"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            submit.mutate();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-[1fr_170px]">
            <Field label="Name" htmlFor="s-name" error={errors.name}>
              <input id="s-name" type="text" value={form.name} onChange={set('name')} required />
            </Field>
            <Field label="What is it?" htmlFor="s-kind" error={errors.kind}>
              <select id="s-kind" value={form.kind} onChange={set('kind')}>
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {kindLabel(k)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-[1fr_100px]">
            <Field label="Town" htmlFor="s-town" error={errors.town}>
              <input id="s-town" type="text" value={form.town} onChange={set('town')} required />
            </Field>
            <Field label="State" htmlFor="s-state" error={errors.state}>
              <input id="s-state" type="text" value={form.state} onChange={set('state')} maxLength={2} placeholder="TN" required />
            </Field>
          </div>
          <Field label="Address or road (optional)" htmlFor="s-addr" error={errors.address}>
            <input id="s-addr" type="text" value={form.address} onChange={set('address')} />
          </Field>
          <Field label="What makes it great?" htmlFor="s-why" error={errors.whyItsGreat}>
            <textarea id="s-why" value={form.whyItsGreat} onChange={set('whyItsGreat')} placeholder="Best burger in the county, pool table, jukebox full of Waylon…" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Your name (optional)" htmlFor="s-you" error={errors.submitterName}>
              <input id="s-you" type="text" value={form.submitterName} onChange={set('submitterName')} />
            </Field>
            <Field label="Your email (optional)" htmlFor="s-email" error={errors.submitterEmail}>
              <input id="s-email" type="email" value={form.submitterEmail} onChange={set('submitterEmail')} />
            </Field>
          </div>
          <button type="submit" className="btn btn-primary self-start" disabled={submit.isPending}>
            {submit.isPending ? 'Sending…' : 'Suggest this place'}
          </button>
          {submit.error && !Object.keys(errors).length && <ErrorNote error={submit.error} />}
        </form>
      )}
    </section>
  );
}
